import { 
  Box, 
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { ApiService } from "../../../../Services/ApiService";
import { useLocation, useNavigate } from "react-router-dom";

// Extracted modules
import { useUnitConfigurationData } from './UnitConfigurationEditorTypes';
import { 
  LoadingIndicator,
  ActionButtons,
  SectionButtons,
  BaseConfigurationForm,
  SectionConfigurationView
} from './UnitConfigurationEditorComponents';
import {
  createHandleBaseChange,
  createHandleMultiSectionChange,
  createHandleSetSectionCount,
  createHandlePHETypeChange
} from './UnitConfigurationEditorEventHandlers';
import {
  createSaveConfiguration,
  createGoToPropertySpecificationEditor,
  createGoToPropertyRangeSpecificationEditor
} from './UnitConfigurationEditorApi';
import { getEditorStyles } from './UnitConfigurationEditorUtils';

/**
 * UnitConfigurationEditor component for creating and editing unit configurations
 */
export const UnitConfigurationEditor: React.FC = () => {
    const ctx = useMsal();
    const apiService = useMemo(() => new ApiService(ctx), [ctx]);
    const navigate = useNavigate();
    const location = useLocation();
    const state: any = location.state;
    const isEdit = Boolean(state);

    // Use custom hook for data loading and state management
    const {
      isLoading,
      unitConfig,
      setUnitConfig,
      originalBasicDetails,
      section, // eslint-disable-line @typescript-eslint/no-unused-vars
      setSection, // eslint-disable-line @typescript-eslint/no-unused-vars
      sectionCollection,
      setSectionCollection,
      amountOfSections,
      setAmountOfSections,
      isNewCreated,
      setIsNewCreated,
      propSpecId,
      userInputs,
      assignOperationTargetValue,
      setAssignOperationValue,
      pheTypesOptions,
      inputOutputOptions,
      flowchartSelectableProperties,
      setFlowchartSelectableProperties,
      selectedPHETypes,
      setSelectedPHETypes,
      loadInitialData
    } = useUnitConfigurationData(apiService, isEdit, state);
    
    // UI state
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState<string>();

    // Get styles
    const styles = getEditorStyles();

    // Create event handlers using the extracted factory functions
    const handleBaseChange = createHandleBaseChange(unitConfig, setUnitConfig, originalBasicDetails);
    const handleMultiSectionChange = createHandleMultiSectionChange(
      sectionCollection,
      setSectionCollection,
      flowchartSelectableProperties,
      setFlowchartSelectableProperties
    );
    const handleSetSectionCount = createHandleSetSectionCount(
      setAmountOfSections,
      sectionCollection,
      setSectionCollection,
      selectedPHETypes
    );
    // Create the correct PHE type handler that updates both unitConfig and sections
    const handlePHETypeChange = createHandlePHETypeChange(
      selectedPHETypes,
      setSelectedPHETypes,
      unitConfig,
      setUnitConfig,
      sectionCollection,
      setSectionCollection
    );
    
    // Create a wrapper function that converts array-based signature to individual value-based signature
    const handlePHETypeChangeWrapper = (newSelection: string[]) => {
      // Get current selection
      const currentSelection = unitConfig?.pheTypes || [];
      
      // Find added items
      const addedItems = newSelection.filter(item => !currentSelection.includes(item));
      // Find removed items
      const removedItems = currentSelection.filter(item => !newSelection.includes(item));
      
      // Process added items
      addedItems.forEach(item => {
        handlePHETypeChange(item, true);
      });
      
      // Process removed items
      removedItems.forEach(item => {
        handlePHETypeChange(item, false);
      });
    };

    // Create API functions using the extracted factory functions
    const saveConfiguration = createSaveConfiguration(
      apiService,
      unitConfig,
      sectionCollection,
      isEdit,
      isNewCreated,
      setIsNewCreated,
      setIsSaving,
      setSaveMessage,
      loadInitialData,
                propSpecId,
      userInputs,
      assignOperationTargetValue
    );

    const goToPropertySpecificationEditor = createGoToPropertySpecificationEditor(
      navigate,
      unitConfig,
      sectionCollection.length
    );

    const goToPropertyRangeSpecificationEditor = createGoToPropertyRangeSpecificationEditor(
      navigate,
      unitConfig,
      sectionCollection
    );

    return (
        <Box sx={styles.container}>
            <Stack spacing={3} sx={styles.contentWrapper}>
                {/* Top buttons row */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    mt: 1
                }}>
                    {/* Back button on left */}
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/admin")}
                        sx={{ textTransform: 'none' }}
                    >
                        Back
                    </Button>
                    
                    {/* Action buttons on right */}
                    <ActionButtons
                        isSaving={isSaving}
                        saveMessage={saveMessage}
                        onSave={saveConfiguration}
                        onPropertySpec={goToPropertySpecificationEditor}
                        onPropertyRangeSpec={goToPropertyRangeSpecificationEditor}
                        isNewCreated={isNewCreated}
                        isEdit={isEdit}
                    />
                </Box>
                
                {/* Title centered below */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="h5" fontWeight="bold">
                        {isEdit ? 'Edit Unit Configuration' : 'Create Unit Configuration'}
                    </Typography>
                </Box>
                
                {isLoading ? (
                    <LoadingIndicator />
                ) : (
                    <Stack spacing={3} width="100%">
                        {/* Section count buttons (only for new configuration) */}
                        {!isEdit && (
                            <Paper sx={{border: 'none', boxShadow: 'none'}}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Number of Sections
                                </Typography>
                                <SectionButtons 
                                    maxSections={5} 
                                    currentSections={amountOfSections} 
                                    onSetSectionCount={handleSetSectionCount} 
                                />
                            </Paper>
                        )}
                        
                        {/* Main form */}
                        <BaseConfigurationForm
                            unitConfig={unitConfig}
                            pheTypesOptions={pheTypesOptions}
                            assignOperationTargetValue={assignOperationTargetValue}
                            isEdit={isEdit}
                            sectionCount={amountOfSections}
                            onBaseChange={handleBaseChange}
                            onPHETypeChange={handlePHETypeChangeWrapper}
                            onOperationTargetValueChange={setAssignOperationValue}
                         />
                        
                        {/* Section configurations */}
                        {!isLoading && (
                            <SectionConfigurationView
                                sectionCollection={sectionCollection}
                                unitConfig={unitConfig}
                                flowchartSelectableProperties={flowchartSelectableProperties}
                                isEdit={isEdit}
                                isNewCreated={isNewCreated}
                                isLoading={isLoading}
                                onSectionChange={handleMultiSectionChange}
                                onSetActiveSection={() => {}}
                                inputOutputOptions={inputOutputOptions}
                            />
                        )}
                    </Stack>
                )}
            </Stack>
        </Box>
    );
}; 